import { useState } from "react"
import withModal from "../../utils/hoc/withModal"
import { saveBankDetails, validateIFSC as validateIFSCapi } from "../../services/api/wallet"

interface AddPayoutAccountProps {
    onSuccess: () => void,
    onClose: () => void
}

export interface AccountDetails {
    bank_account_number: string,
    bank_ifsc: string,
    type: 'bank_account'
}

interface ValidationResult {
    status: boolean,
    message?: string,
}

interface ValidationResults {
    bank_account_number: ValidationResult | null,
    bank_ifsc: ValidationResult | null
}

const AddPayoutAccount: React.FC<AddPayoutAccountProps> = ({ onSuccess, onClose }) => {

    const [isProcessing, setProcessing] = useState<boolean>(false);

    const [accountDetails, setAccountDetails] = useState<AccountDetails>({
        bank_account_number: '',
        bank_ifsc: '',
        type: 'bank_account'
    });

    const [fetchedBank, setFetchedBank] = useState<any | null>(null);

    const [validationResults, setValidationResults] = useState<ValidationResults>({
        bank_account_number: null,
        bank_ifsc: null
    });

    const validateIFSC = async (e: any) => {
        setValidationResults({ ...validationResults, bank_ifsc: null });
        setFetchedBank(null);
        const ifsc = accountDetails.bank_ifsc.toUpperCase();
        const regex = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;
        let valid = regex.test(ifsc);

        let message = 'Please enter valid IFSC';

        if (valid) {
            try {
                const result = await validateIFSCapi(ifsc);
                valid = result.status;
                if (result.bank) setFetchedBank(result.bank);
            } catch (error) {
                console.log(error);
                valid = false;
                message = "There was some issue validating your IFSC, please try again."
            }
        }

        setValidationResults({ ...validationResults, bank_ifsc: { status: valid, ...(!valid && { message }) } });
        if (valid) setAccountDetails({ ...accountDetails, bank_ifsc: ifsc })
    }

    const validateAccountNumber = () => {
        setValidationResults({ ...validationResults, bank_account_number: null });
        let status = true;
        let message = '';
        if (accountDetails.bank_account_number.length < 6 || accountDetails.bank_account_number.length > 40) {
            status = false;
            message= 'Please enter a valid account number.'
        }
        setValidationResults({ ...validationResults, bank_account_number: { status, message } })
    }

    const savePayoutDetails = async () => {
        if(isAccountNumberValid(false)) return;
        setValidationResults({ ...validationResults, bank_account_number: null });
        setProcessing(true);
        try {
            const result = await saveBankDetails(accountDetails);
            if (result) onSuccess();
        } catch (error) {
            setValidationResults({ ...validationResults, bank_account_number: { status: false, message: 'Please enter a valid account number.' } })
        } finally {
            setProcessing(false);
        }
    }

    const isIFSCvalid = (validity: boolean): boolean => {
        return !!(validationResults.bank_ifsc && validationResults.bank_ifsc.status === validity);
    }

    const isAccountNumberValid = (validity: boolean): boolean => {
        return !!(validationResults.bank_account_number && validationResults.bank_account_number.status === validity);
    }

    const allValidated = () => {
        return !!(fetchedBank && 
            validationResults.bank_ifsc &&
            validationResults.bank_ifsc.status === true
        );
    }



    return (
        <div>
            <div className="menu-title p-3 mb-0">
                <h4 className="font-800">Bank Details</h4>
                <button className="close-menu" onClick={onClose}>
                    <i className="fa fa-times-circle"></i>
                </button>
            </div>
            <div className="content mt-0 pb-4">
                <div className="divider mb-3"></div>
                <div className="d-flex flex-column">
                    <div className="input-style has-borders no-icon input-style-always-active validate-field mb-0">
                        <input type="text" autoComplete="off" className={`form-control mb-3 validate-number ${isIFSCvalid(false) && 'is-invalid'}`} id="ifsc" placeholder="IFSC"
                            value={accountDetails.bank_ifsc}
                            onChange={e => setAccountDetails({ ...accountDetails, bank_ifsc: e.target.value })}
                            onBlur={validateIFSC}
                        />
                        <label htmlFor="ifsc" className={`font-12 font-500 ${isIFSCvalid(false) ? 'text-danger' : 'color-highlight'}`}>IFSC</label>
                        {
                            isIFSCvalid(false) &&
                            <i className="fas fa-exclamation-circle invalid color-red-dark"></i>
                        }
                        {
                            isIFSCvalid(true) &&
                            <i className="fa fa-check valid color-green-dark"></i>
                        }
                        {
                            !accountDetails.bank_ifsc &&
                            <em>(required)</em>
                        }
                    </div>
                    {
                        isIFSCvalid(false) &&
                        <span className="text-danger mt-n3 ms-2 font-10">{validationResults.bank_ifsc?.message}</span>
                    }
                    {
                        isIFSCvalid(true) && fetchedBank &&
                        <span className="text-success mt-n3 ms-2 font-10">{fetchedBank.name}, {fetchedBank.branch_name}</span>
                    }

                    <div className="input-style has-borders no-icon input-style-always-active validate-field mb-0 mt-2">
                        <input type="text" autoComplete="off" className={`form-control mb-3 validate-number ${isAccountNumberValid(false) && 'is-invalid'}`} id="account-number" placeholder="Account Number"
                            value={accountDetails.bank_account_number}
                            onChange={e => setAccountDetails({ ...accountDetails, bank_account_number: e.target.value })}
                            onBlur={validateAccountNumber}
                        />
                        <label htmlFor="account-number" className={`font-12 font-500 ${isAccountNumberValid(false) ? 'text-danger' : 'color-highlight'}`}>Account Number</label>
                        {
                            isAccountNumberValid(false) &&
                            <i className="fas fa-exclamation-circle invalid color-red-dark"></i>
                        }
                        {
                            isAccountNumberValid(true) &&
                            <i className="fa fa-check valid color-green-dark"></i>
                        }
                        {
                            !accountDetails.bank_ifsc &&
                            <em>(required)</em>
                        }
                    </div>
                    {
                        isAccountNumberValid(false) &&
                        <span className="text-danger mt-n3 ms-2 font-10">{validationResults.bank_account_number?.message}</span>
                    }

                </div>
                <div className="divider my-3 mt-0"></div>
                <button disabled={isProcessing || !allValidated()} className="btn btn-sm w-100 btn-primary rounded-sm mb-2" onClick={savePayoutDetails}>{isProcessing ? 'Validating...' : 'Save Details'}</button>
            </div>
        </div>
    )
}
export default withModal(AddPayoutAccount)